/**
 * UnsafeAreaAlert — ops banner when pickup/delivery matches unsafe areas.
 * MUI variant for lead details; Ant Design variant for booking modal.
 */

import React from "react";
import { Alert as MuiAlert, Box, Typography } from "@mui/material";
import { Alert as AntdAlert, Typography as AntdTypography, Tag } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import {
  formatUnsafeAreaTitle,
  hasUnsafeAreaMatches,
  unsafeAreaAntdType,
  unsafeAreaMuiSeverity,
} from "../utils/unsafeAreaAlert.js";

const { Text: AntdText } = AntdTypography;

/**
 * @param {Object} props
 * @param {Object} props.hit
 * @param {"mui"|"antd"} props.variant
 */
function HitDetails({ hit, variant }) {
  const sideLabel = hit.side === "delivery" ? "Delivery" : "Pickup";
  const protocol = hit.generalSafetyProtocol || hit.notes;
  const tierTagLabel = hit.tier
    ? `Tier ${hit.tier}${
        hit.riskCategory
          ? ` · ${hit.riskCategory.replace(/^Tier \d+:\s*/, "")}`
          : ""
      }`
    : null;

  if (variant === "antd") {
    return (
      <AntdText style={{ display: "block", marginBottom: 8 }}>
        <AntdText strong>
          {sideLabel}: {hit.name}{" "}
        </AntdText>
        {tierTagLabel ? (
          <Tag
            color={
              hit.severity === "critical" || hit.severity === "high"
                ? "red"
                : "orange"
            }
          >
            {tierTagLabel}
          </Tag>
        ) : null}
        {hit.primaryLogisticsConcern ? (
          <AntdText style={{ display: "block" }}>
            Concern: {hit.primaryLogisticsConcern}
          </AntdText>
        ) : null}
        {protocol ? (
          <AntdText style={{ display: "block" }}>
            Protocol: {protocol}
          </AntdText>
        ) : null}
      </AntdText>
    );
  }

  return (
    <Box mb={1}>
      <Typography variant="body2" component="div" fontWeight={600}>
        {sideLabel}: {hit.name}
        {hit.tier ? ` · Tier ${hit.tier}` : ""}
        {hit.riskCategory ? ` (${hit.riskCategory})` : ""}
      </Typography>
      {hit.primaryLogisticsConcern ? (
        <Typography variant="body2" component="div" color="text.secondary">
          Concern: {hit.primaryLogisticsConcern}
        </Typography>
      ) : null}
      {protocol ? (
        <Typography variant="body2" component="div" color="text.secondary">
          Protocol: {protocol}
        </Typography>
      ) : null}
    </Box>
  );
}

/**
 * @param {Object} props
 * @param {{ matched?: boolean, highestSeverity?: string|null, hits?: Array }} props.matches
 * @param {"mui"|"antd"} [props.variant]
 * @param {Object} [props.sx]
 * @param {Object} [props.style]
 */
const UnsafeAreaAlert = ({ matches, variant = "mui", sx, style }) => {
  if (!hasUnsafeAreaMatches(matches)) {
    return null;
  }

  const title = formatUnsafeAreaTitle(matches);
  const lines = matches.hits || [];

  if (variant === "antd") {
    return (
      <AntdAlert
        type={unsafeAreaAntdType(matches.highestSeverity)}
        showIcon
        icon={<WarningOutlined />}
        message={title}
        description={
          <>
            {lines.map((hit, index) => (
              <HitDetails
                key={`${hit.side}-${hit.name}-${index}`}
                hit={hit}
                variant="antd"
              />
            ))}
          </>
        }
        style={{ marginBottom: 16, ...style }}
      />
    );
  }

  return (
    <MuiAlert
      severity={unsafeAreaMuiSeverity(matches.highestSeverity)}
      sx={{ mb: 2, ...sx }}
    >
      <Typography variant="subtitle2" component="div" fontWeight={600}>
        {title}
      </Typography>
      <Box mt={0.5}>
        {lines.map((hit, index) => (
          <HitDetails
            key={`${hit.side}-${hit.name}-${index}`}
            hit={hit}
            variant="mui"
          />
        ))}
      </Box>
    </MuiAlert>
  );
};

export default UnsafeAreaAlert;
